import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  api,
  type Asesor,
  type Contacto,
  type CasoDetalle,
  type EvidenciaOut,
  type ComentarioClienteOut,
  type CorreoEnviarIn,
  type CorreoOut,
  type CorreoPreviewOut,
  type PaqueteOut,
  type Proveedor,
  type ProveedorContacto,
  type UserMini,
} from "@/lib/api";
import BitacoraReferencia from "@/components/BitacoraReferencia";
import GenerarComentarioIA from "@/components/correo/GenerarComentarioIA";
import InsertarImagenModal from "@/components/InsertarImagenModal";
import RichTextEditor, {
  type RichTextEditorHandle,
} from "@/components/RichTextEditor";
import {
  ChipsField,
  Label,
  type Destinatario,
} from "@/components/correo/DestinatariosChips";
import { construirContactosUnificados } from "@/components/correo/contactosUnificados";
import {
  PreguntaImagenModal,
  PreguntaVerInsertarModal,
  PreguntaOversizeModal,
} from "@/components/correo/PreguntasAdjuntoModals";
import ProgramarEnvioModal from "@/components/correo/ProgramarEnvioModal";
import SeccionPlegable from "@/components/correo/SeccionPlegable";
import { useAdjuntosCorreo } from "@/components/correo/useAdjuntosCorreo";
import SelectorEvidenciasModal from "@/components/correo/SelectorEvidenciasModal";
import {
  MAX_ADJUNTO_BYTES,
  MAX_TOTAL_ADJUNTOS_BYTES,
  fmtMB,
} from "@/components/correo/utilsCorreo";
import Modal from "@/ui/Modal";
import AyudaVista from "@/ui/AyudaVista";
import VisorArchivo from "@/ui/VisorArchivo";
import Toggle from "@/ui/Toggle";
import SolucionPicker, { type SolucionSeleccion } from "./SolucionPicker";
import { ayudaEnviarCorreo } from "@/components/ayuda/vistas/enviarCorreo";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/ui/Toast";
import { fmtBytes } from "@/lib/formato";

// Reuso el mismo key que TabCorreos para que el toggle se sincronice
// (todos los previews HTML de correo respetan el mismo modo).
const PREVIEW_LIGHT_KEY = "correos:preview-claro";
// Toggles de contexto del correo al proveedor (recordados por modo).
const INCLUIR_SOLICITUD_KEY = "correos:incluir-solicitud:jimi";
const INCLUIR_GENERALES_KEY = "correos:incluir-generales:jimi";

type Props = {
  caso: CasoDetalle;
  destinatario: "cliente" | "jimi";
  // Pre-cargar el HTML del editor (desde TabComentario o quoted body de
  // Responder/Reenviar en TabCorreos).
  comentarioInicial?: string;
  // Pre-cargar el editor del modo proveedor (Recordar al proveedor).
  comentarioProveedorInicial?: string;
  // Pre-seleccionar la plantilla ("seguimiento" = corto, sin resumen).
  plantillaInicial?: "completa" | "seguimiento";
  // Pre-cargar el asunto override (Re: / Fwd:)
  asuntoInicial?: string;
  // Pre-cargar el destinatario (Responder al sender del IN original)
  toInicial?: string;
  // Hilo: id del correo al que se responde (solo flujo Responder).
  respondeACorreoId?: number;
  // Cita: id del correo cuyo cuerpo se anexa citado al final (backend lo arma).
  citarCorreoId?: number;
  // Modo Reenviar (Fwd estilo Gmail): id del correo a reenviar TAL CUAL.
  reenviarCorreoId?: number;
  onClose: () => void;
  onSent?: (correo: CorreoOut) => void;
};

/**
 * Modal de envio de correo (cliente o JIMI).
 *
 * Flujo:
 *  1. Carga preview del correo con render del backend
 *  2. Muestra preview HTML lado a lado con campos editables (asunto, to, cc)
 *  3. Permite seleccionar un paquete ZIP (URL + password) para incluir
 *  4. Permite marcar como Cierre (afecta titulo y frase de apertura)
 *  5. Enviar -> backend renderiza, persiste y envia via SMTP
 */
/**
 * Cuánto se espera antes de mandar de verdad, para poder deshacer.
 *
 * Reusa la cola de correos programados: el worker despierta cada 60 s, así que
 * el correo sale entre uno y dos minutos después. Para soporte da igual, y a
 * cambio el envío sobrevive a un reinicio y se puede cancelar desde la pestaña
 * Correos aunque se pierda el aviso.
 */
const VENTANA_DESHACER_MS = 60_000;

function enUnMinuto(): string {
  return new Date(Date.now() + VENTANA_DESHACER_MS).toISOString();
}

/** «Nombre · correo» del asesor sugerido: el correo antes solo se veía en un
 *  tooltip, y en el celular no había forma de saber a qué dirección iba. */
function etiquetaAsesor(a: { nombre?: string | null; email: string }): string {
  return a.nombre ? `${a.nombre} · ${a.email}` : a.email;
}

export default function EnviarCorreoModal({
  caso,
  destinatario,
  comentarioInicial,
  comentarioProveedorInicial,
  plantillaInicial,
  asuntoInicial,
  toInicial,
  respondeACorreoId,
  citarCorreoId,
  reenviarCorreoId,
  onClose,
  onSent,
}: Props) {
  const toast = useToast();
  // Solo para la Ayuda: la sección de IA se monta sola (`GenerarComentarioIA`
  // se gatea con el mismo flag) y la Ayuda no debe explicar lo que no se ve.
  const iaHabilitada = useAuth((s) => Boolean(s.user?.ai_comentarios_enabled));
  // Modo Reenviar (Fwd estilo Gmail): manda el original tal cual, sin plantilla.
  const esReenvio = reenviarCorreoId != null;
  const [paquetes, setPaquetes] = useState<PaqueteOut[]>([]);
  const [paqueteId, setPaqueteId] = useState<number | "">("");
  const [esCierre, setEsCierre] = useState(false);
  // "Enviar y cerrar caso": si el envio es inmediato, cierra el caso al
  // mandarse; si es programado, deja una marca y el worker cierra el
  // caso cuando mande el correo (worker correos_programados).
  const [cerrarCasoAlEnviar, setCerrarCasoAlEnviar] = useState(false);
  const [solucion, setSolucion] = useState<SolucionSeleccion>({
    tipoSolucionId: null,
    solucionesImei: null,
  });
  const [programarOpen, setProgramarOpen] = useState(false);
  const [plantilla, setPlantilla] = useState<"completa" | "seguimiento">(
    plantillaInicial ?? "completa",
  );
  // comentario es HTML (TipTap) — se manda al backend como comentario_cliente_html.
  // El field legacy comentario_cliente_humano siempre va NULL ahora.
  const [comentario, setComentario] = useState(comentarioInicial || "");
  // Para modo proveedor: HTML del editor TipTap inline (custom, opcional)
  const [comentarioProveedor, setComentarioProveedor] = useState(
    comentarioProveedorInicial || "",
  );
  // Empezamos vacio — el sync de destinatarios lo llena con el nombre del
  // primer contacto cargado (caso comun). Si queda vacio al enviar, el
  // backend usa "Hola," sin nombre en lugar de inventar uno.
  const [contactoProveedor, setContactoProveedor] = useState("");
  const [previewLight, setPreviewLight] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PREVIEW_LIGHT_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Contexto del correo al proveedor. Default OFF (no incluir) en proveedor —
  // lo más común es enfocar el correo en lo que pides, no en el reporte del
  // cliente. Se recuerda en localStorage.
  const [incluirSolicitud, setIncluirSolicitud] = useState<boolean>(() => {
    try {
      return localStorage.getItem(INCLUIR_SOLICITUD_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [incluirGenerales, setIncluirGenerales] = useState<boolean>(() => {
    try {
      return localStorage.getItem(INCLUIR_GENERALES_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [toOverride, setToOverride] = useState(toInicial || "");
  const [cc, setCc] = useState("");
  const [asuntoOverride, setAsuntoOverride] = useState(asuntoInicial || "");
  // Destinatarios como chips (TO + CC) — aplica para ambos modos.
  // Para QUIÉN es el correo. Decide la plantilla (a un compañero no se le
  // dice «tu equipo») y calibra a la IA. Solo aplica en el modo cliente:
  // al proveedor se le escribe siempre igual.
  const [audiencia, setAudiencia] = useState<"cliente" | "interno">("cliente");
  const [destinatariosTo, setDestinatariosTo] = useState<Destinatario[]>([]);
  const [destinatariosCc, setDestinatariosCc] = useState<Destinatario[]>([]);
  const [proveedoresAll, setProveedoresAll] = useState<Proveedor[]>([]);
  const [contactosCliente, setContactosCliente] = useState<Contacto[]>([]);
  const [asesores, setAsesores] = useState<Asesor[]>([]);
  const [usuarios, setUsuarios] = useState<UserMini[]>([]);
  // Email del destinatario del "Para" al que se saluda (modo cliente).
  const [saludoEmail, setSaludoEmail] = useState<string>("");
  // Selector "+ Agregar contacto" (state separado para TO y CC)
  const [agregarOpenTo, setAgregarOpenTo] = useState(false);
  const [agregarOpenCc, setAgregarOpenCc] = useState(false);
  const [agregarEmailTo, setAgregarEmailTo] = useState("");
  const [agregarNombreTo, setAgregarNombreTo] = useState("");
  const [agregarEmailCc, setAgregarEmailCc] = useState("");
  const [agregarNombreCc, setAgregarNombreCc] = useState("");
  // El asunto sigue al que genera el backend (cambia con "Es cierre",
  // plantilla, etc.) MIENTRAS el user no lo edite a mano. Si llega
  // asuntoInicial (flujos como responder), se respeta desde el inicio.
  const asuntoEditadoRef = useRef(Boolean(asuntoInicial && asuntoInicial.trim()));
  // Modal de Evidencias: se abre cuando el user arrastra una imagen >1MB
  // o hace click en el boton imagen del toolbar del editor.
  const [imagenModalOpen, setImagenModalOpen] = useState(false);
  const [imagenTarget, setImagenTarget] = useState<"cliente" | "proveedor">("cliente");
  const editorClienteRef = useRef<RichTextEditorHandle>(null);
  const editorProveedorRef = useRef<RichTextEditorHandle>(null);
  // Auto-save del draft del Mensaje al cliente — vive en
  // comentarios_cliente.is_draft=true. Persiste si cierras el modal
  // sin enviar; al abrir otra vez, se recarga.
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const skipNextDraftSaveRef = useRef(false);
  // Historial de borradores formales (is_draft=false, no enviados)
  // para reusar como plantilla.
  const [historial, setHistorial] = useState<ComentarioClienteOut[]>([]);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [preview, setPreview] = useState<CorreoPreviewOut | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api
      .get<PaqueteOut[]>(`/api/casos/${caso.id}/paquetes`)
      .then((list) => {
        const vigentes = list.filter(
          (p) => new Date(p.expira_at).getTime() > Date.now(),
        );
        setPaquetes(vigentes);
      })
      .catch(() => toast.error("No pude cargar los paquetes del caso"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  // Cargar draft activo + historial de borradores formales (modo cliente).
  // Si el user paso comentarioInicial (ej. desde Reenviar/Responder con
  // quoted body), ese gana sobre el draft.
  useEffect(() => {
    if (destinatario !== "cliente") {
      setDraftLoaded(true);
      return;
    }
    let cancelled = false;
    Promise.all([
      api
        .get<ComentarioClienteOut | null>(
          `/api/casos/${caso.id}/comentarios-cliente/draft`,
        )
        .catch(() => null),
      api
        .get<ComentarioClienteOut[]>(`/api/casos/${caso.id}/comentarios-cliente`)
        .catch(() => []),
    ]).then(([draft, list]) => {
      if (cancelled) return;
      // Si comentarioInicial vino del padre (ej. quoted body de Responder),
      // ese gana — no pisamos. Solo cargamos draft si comentario esta vacio.
      if (draft?.texto_html && !comentarioInicial && !citarCorreoId && !esReenvio) {
        skipNextDraftSaveRef.current = true;
        setComentario(draft.texto_html);
      }
      // Historial: solo borradores NO enviados (formales is_draft=false)
      setHistorial(list.filter((c) => !c.is_draft && !c.enviado_por_correo));
      setDraftLoaded(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id, destinatario]);

  // Auto-save del draft (modo cliente) con debounce 700ms
  useEffect(() => {
    if (destinatario !== "cliente") return;
    if (citarCorreoId || esReenvio) return; // reply/forward/reminder: no ensucia el draft del cliente
    if (!draftLoaded) return;
    if (skipNextDraftSaveRef.current) {
      skipNextDraftSaveRef.current = false;
      return;
    }
    const handle = setTimeout(() => {
      // Vacio: borrar draft (si existia)
      if (!comentario.trim()) {
        void api
          .del(`/api/casos/${caso.id}/comentarios-cliente/draft`)
          // Silencio a propósito: limpieza de draft en background.
          .catch(() => {});
        return;
      }
      setSavingDraft(true);
      void api
        .put(`/api/casos/${caso.id}/comentarios-cliente/draft`, {
          texto: comentario.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
          texto_html: comentario,
        })
        // Silencio a propósito: autosave en background (un toast por
        // cada fallo de autoguardado sería ruido insoportable).
        .catch(() => {})
        .finally(() => setSavingDraft(false));
    }, 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comentario, destinatario, draftLoaded]);

  function reusarBorrador(c: ComentarioClienteOut) {
    skipNextDraftSaveRef.current = false; // permitimos que el sync guarde
    setComentario(c.texto_html || c.texto);
    setHistorialOpen(false);
    toast.success("Borrador cargado al editor");
  }

  function editorActivo(): RichTextEditorHandle | null {
    return destinatario === "cliente"
      ? editorClienteRef.current
      : editorProveedorRef.current;
  }

  // Adjuntos SMTP del modal (drag-drop, chips, oversize, inline-vs-attach):
  // toda la lógica vive en el hook — aquí solo consumimos los mismos nombres.
  const {
    adjuntos,
    subiendoAdjuntos,
    totalAdjuntosBytes,
    dragOver,
    askImagen,
    setAskImagen,
    askOversize,
    setAskOversize,
    subirAdjuntoNormal,
    subirOversize,
    insertarImagenInline,
    askImagenEvidencia,
    setAskImagenEvidencia,
    adjuntarEvidencia,
    insertarEvidenciaInline,
    handleFiles,
    removerAdjunto,
    onModalDragOver,
    onModalDragLeave,
    onModalDrop,
  } = useAdjuntosCorreo({ casoId: caso.id, editorActivo });
  const [selEvidenciasOpen, setSelEvidenciasOpen] = useState(false);
  // Imagen de evidencia clickeada: primero Visualizar vs Insertar; luego el visor.
  const [decidirEvidencia, setDecidirEvidencia] = useState<EvidenciaOut | null>(null);
  const [verEvidencia, setVerEvidencia] = useState<EvidenciaOut | null>(null);

  // Inicializacion del TO/CC segun modo:
  // - Cliente: TO = contacto principal del caso; CC = seguimientos del
  //   caso que tengan email registrado.
  // - Proveedor: contactos del proveedor de la marca del caso (CC vacio).
  // En ambos: cargar todos los proveedores para el selector "+ Agregar".
  useEffect(() => {
    if (destinatario === "cliente") {
      // TO: si vino un destinatario inicial (Responder → sender del IN
      // original), ese manda; el catálogo solo aporta el nombre si la
      // dirección coincide con un contacto del caso. Sin toInicial,
      // TO = principal del caso (caso.contacto_* viene del backend ya
      // derivado de la pivote, con rol='principal').
      if (toInicial && destinatariosTo.length === 0) {
        const conocidos = [
          ...(caso.contacto_email
            ? [
                {
                  nombre: caso.contacto_nombre || caso.contacto_email,
                  email: caso.contacto_email,
                },
              ]
            : []),
          ...(caso.contactos ?? [])
            .filter((c) => c.email)
            .map((c) => ({ nombre: c.nombre, email: c.email as string })),
        ];
        const match = conocidos.find(
          (d) => d.email.toLowerCase() === toInicial.toLowerCase(),
        );
        setDestinatariosTo([match ?? { nombre: toInicial, email: toInicial }]);
      } else if (caso.contacto_email && destinatariosTo.length === 0 && !esReenvio) {
        setDestinatariosTo([
          {
            nombre: caso.contacto_nombre || caso.contacto_email,
            email: caso.contacto_email,
          },
        ]);
      }
      // CC = seguimientos con email. Solo lo seedeamos si el CC sigue
      // vacio (no pisar lo que el user ya edito).
      if (destinatariosCc.length === 0 && caso.contactos && !esReenvio) {
        const seguimientosConEmail = caso.contactos
          .filter((c) => c.rol === "seguimiento" && c.email)
          .map((c) => ({ nombre: c.nombre, email: c.email as string }));
        if (seguimientosConEmail.length > 0) {
          setDestinatariosCc(seguimientosConEmail);
        }
      }
    } else if (destinatario === "jimi") {
      // Con toInicial (Recordar al proveedor → destinatario original),
      // ese manda: se siembra de inmediato y el catálogo solo aporta el
      // nombre para el chip y el saludo si la dirección coincide.
      if (toInicial && destinatariosTo.length === 0) {
        setDestinatariosTo([{ nombre: toInicial, email: toInicial }]);
      }
      void api
        .get<ProveedorContacto[]>(`/api/casos/${caso.id}/proveedor-contactos`)
        .then((list) => {
          if (toInicial) {
            const match = list.find(
              (c) => c.email.toLowerCase() === toInicial.toLowerCase(),
            );
            if (match) {
              setDestinatariosTo([{ nombre: match.nombre, email: match.email }]);
              setContactoProveedor(match.nombre);
            }
            return;
          }
          const principal = list.find((c) => c.es_principal);
          if (principal) {
            setDestinatariosTo([{ nombre: principal.nombre, email: principal.email }]);
            setContactoProveedor(principal.nombre);
          } else if (list.length > 0) {
            setDestinatariosTo([{ nombre: list[0].nombre, email: list[0].email }]);
            setContactoProveedor(list[0].nombre);
          }
        })
        .catch(() => toast.error("No pude cargar los contactos del proveedor"));
    }
    void api
      .get<Proveedor[]>("/api/proveedores")
      .then(setProveedoresAll)
      .catch(() => toast.error("No pude cargar la lista de proveedores"));
    if (caso.cliente_id) {
      void api
        .get<Contacto[]>(`/api/clientes/${caso.cliente_id}/contactos`)
        .then(setContactosCliente)
        .catch(() => {});
    }
    void api.get<Asesor[]>("/api/asesores").then(setAsesores).catch(() => {});
    // Equipo interno (soporte/admin) para poder ponerlos en copia sin teclear.
    void api.get<UserMini[]>("/api/users/asignables").then(setUsuarios).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id, destinatario]);

  // Sync chips TO -> toOverride string (lo que el backend recibe)
  useEffect(() => {
    setToOverride(destinatariosTo.map((d) => d.email).join(", "));
    // En modo proveedor, llenar el saludo con el primer contacto si esta vacio
    if (destinatario === "jimi" && destinatariosTo.length > 0 && !contactoProveedor.trim()) {
      setContactoProveedor(destinatariosTo[0].nombre);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinatariosTo, destinatario]);

  // Sync chips CC -> cc string
  useEffect(() => {
    setCc(destinatariosCc.map((d) => d.email).join(", "));
  }, [destinatariosCc]);

  // Mantener el asunto en sync con el que genera el backend (cambia con
  // "Es cierre", plantilla, etc.) mientras el user no lo haya editado a mano.
  useEffect(() => {
    if (!preview?.asunto) return;
    if (asuntoEditadoRef.current) return;
    setAsuntoOverride(preview.asunto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.asunto]);

  // Helpers para TO
  function removerDestTo(idx: number) {
    setDestinatariosTo((prev) => prev.filter((_, i) => i !== idx));
  }
  function pickContactoTo(nombre: string, email: string) {
    setDestinatariosTo((prev) =>
      prev.some((d) => d.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { nombre, email }],
    );
    setAgregarOpenTo(false);
  }
  function agregarLibreTo() {
    if (!agregarEmailTo.trim()) return;
    const email = agregarEmailTo.trim();
    const nombre = agregarNombreTo.trim() || email.split("@")[0];
    setDestinatariosTo((prev) =>
      prev.some((d) => d.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { nombre, email }],
    );
    setAgregarEmailTo("");
    setAgregarNombreTo("");
    setAgregarOpenTo(false);
  }

  // Helpers para CC
  function removerDestCc(idx: number) {
    setDestinatariosCc((prev) => prev.filter((_, i) => i !== idx));
  }
  function pickContactoCc(nombre: string, email: string) {
    setDestinatariosCc((prev) =>
      prev.some((d) => d.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { nombre, email }],
    );
    setAgregarOpenCc(false);
  }
  function agregarLibreCc() {
    if (!agregarEmailCc.trim()) return;
    const email = agregarEmailCc.trim();
    const nombre = agregarNombreCc.trim() || email.split("@")[0];
    setDestinatariosCc((prev) =>
      prev.some((d) => d.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { nombre, email }],
    );
    setAgregarEmailCc("");
    setAgregarNombreCc("");
    setAgregarOpenCc(false);
  }

  // Contactos guardados unificados (proveedores + cliente del caso + asesores)
  // para el buscador del selector "+ Agregar contacto".
  const contactosUnificados = useMemo(
    () =>
      construirContactosUnificados(
        proveedoresAll,
        contactosCliente,
        asesores,
        usuarios,
      ),
    [proveedoresAll, contactosCliente, asesores, usuarios],
  );

  // Nombre del saludo (modo cliente): sigue al destinatario elegido en "Para".
  // Default = el primero del "Para"; con >=2 el user elige con "Saludar a".
  const saludoNombre = useMemo(() => {
    if (destinatario !== "cliente") return "";
    const elegido = destinatariosTo.find((d) => d.email === saludoEmail);
    return (elegido ?? destinatariosTo[0])?.nombre ?? "";
  }, [destinatario, destinatariosTo, saludoEmail]);

  // Render del preview cuando cambian los inputs
  useEffect(() => {
    const id = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const p = await api.post<CorreoPreviewOut>(
          `/api/casos/${caso.id}/correos/preview`,
          {
            destinatario,
            audiencia,
            es_cierre: esCierre,
            plantilla,
            comentario_cliente_humano: null, // legacy, ya no se usa
            seguimiento_humano: null, // legacy, ya no se usa
            comentario_cliente_html:
              destinatario === "cliente" && comentario.trim() ? comentario : null,
            comentario_proveedor_html:
              destinatario === "jimi" && comentarioProveedor.trim()
                ? comentarioProveedor
                : null,
            contacto_proveedor:
              destinatario === "jimi" ? contactoProveedor : "",
            saludo_nombre:
              destinatario === "cliente" ? saludoNombre || null : null,
            paquete_id: paqueteId === "" ? null : paqueteId,
            incluir_solicitud: destinatario === "jimi" ? incluirSolicitud : true,
            incluir_generales: destinatario === "jimi" ? incluirGenerales : true,
            // Hilo: id del correo al que se responde (flujo "Recordar al
            // proveedor"); el preview lo usa para citar el correo anterior.
            responde_a_correo_id: respondeACorreoId ?? null,
            citar_correo_id: citarCorreoId ?? null,
            reenviar_correo_id: reenviarCorreoId ?? null,
          },
        );
        setPreview(p);
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("Vista previa falló", msg);
      } finally {
        setPreviewLoading(false);
      }
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinatario, audiencia, plantilla, esCierre, comentario, comentarioProveedor, contactoProveedor, saludoNombre, paqueteId, incluirSolicitud, incluirGenerales, respondeACorreoId, citarCorreoId, reenviarCorreoId]);

  /**
   * Cancela el correo que acaba de salir a la cola. Vive fuera de `enviar`
   * porque el modal ya se cerró cuando el usuario aprieta «Deshacer»: lo que
   * sigue vivo es el aviso, con esta función capturada.
   */
  async function deshacerEnvio(correoId: number) {
    try {
      await api.post(`/api/casos/${caso.id}/correos/${correoId}/cancelar-programado`);
      toast.info("Envío cancelado", "El correo no se mandó.");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("Ya no pude cancelarlo", msg);
    }
  }

  async function enviar(scheduledAt?: string) {
    if (!preview) return;
    setSubmitting(true);
    try {
      const toList = toOverride.trim()
        ? toOverride.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
        : null;
      const ccList = cc.trim()
        ? cc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
        : null;

      const payload: CorreoEnviarIn = {
        destinatario,
        audiencia,
        es_cierre: esCierre,
        plantilla,
        comentario_cliente_humano: null, // legacy
        seguimiento_humano: null, // legacy
        comentario_cliente_html:
          destinatario === "cliente" && comentario.trim() ? comentario : null,
        comentario_proveedor_html:
          destinatario === "jimi" && comentarioProveedor.trim()
            ? comentarioProveedor
            : null,
        contacto_proveedor: destinatario === "jimi" ? contactoProveedor : undefined,
        saludo_nombre: destinatario === "cliente" ? saludoNombre || null : null,
        paquete_id: paqueteId === "" ? null : paqueteId,
        incluir_solicitud: destinatario === "jimi" ? incluirSolicitud : true,
        incluir_generales: destinatario === "jimi" ? incluirGenerales : true,
        to_override: toList,
        cc: ccList,
        asunto_override: asuntoOverride.trim() || null,
        // Sin fecha explícita, el correo NO sale al instante: se pone en cola con
        // un minuto de gracia para poder deshacerlo. Un correo mandado por error
        // no se puede recoger; un minuto de espera no le duele a nadie.
        scheduled_at: scheduledAt ?? enUnMinuto(),
        cerrar_caso: cerrarCasoAlEnviar,
        tipo_solucion_id: cerrarCasoAlEnviar ? solucion.tipoSolucionId : null,
        soluciones_imei: cerrarCasoAlEnviar ? solucion.solucionesImei : null,
        adjuntos_ids: adjuntos.map((a) => a.id),
        responde_a_correo_id: respondeACorreoId ?? null,
        citar_correo_id: citarCorreoId ?? null,
        reenviar_correo_id: reenviarCorreoId ?? null,
      };

      const enviado = await api.post<CorreoOut>(
        `/api/casos/${caso.id}/correos/enviar`,
        payload,
      );
      if (scheduledAt) {
        toast.success("Correo programado", enviado.asunto);
      } else {
        toast.success("Correo en camino", `Sale en un minuto · ${enviado.asunto}`, {
          duration: VENTANA_DESHACER_MS,
          action: {
            label: "Deshacer",
            onClick: () => void deshacerEnvio(enviado.id),
          },
        });
      }
      // Limpiar el draft activo del caso — el correo enviado ya queda
      // en TabCorreos con badge OUT y no debe re-aparecer al reabrir el modal.
      if (destinatario === "cliente" && !esReenvio) {
        await api
          .del(`/api/casos/${caso.id}/comentarios-cliente/draft`)
          .catch(() => {});
      }
      onSent?.(enviado);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error desconocido";
      toast.error("No se pudo enviar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Si se va a cerrar el caso, exige el tipo de solución (por caso o por
  // equipo, según los IMEIs). Si no se cierra, no estorba.
  const solucionCompleta =
    !cerrarCasoAlEnviar ||
    (caso.imeis.length === 0
      ? solucion.tipoSolucionId != null
      : solucion.solucionesImei
        ? caso.imeis.every((im) => solucion.solucionesImei?.[im.id] != null)
        : solucion.tipoSolucionId != null);

  const titulo = "Enviar correo";
  const descripcion =
    destinatario === "cliente"
      ? `Caso ${caso.folio} · Correo con el diseño de la empresa (logo, datos del cliente y resumen del caso).`
      : `Caso ${caso.folio} · Correo sencillo con saludo y la descripción del problema.`;

  return (
    <Modal
      open={true}
      onOpenChange={(v) => {
        if (!v && !verEvidencia) onClose();
      }}
      title={titulo}
      description={descripcion}
      size="xl"
      ayuda={
        <AyudaVista
          titulo={titulo}
          {...ayudaEnviarCorreo({ destinatario, esReenvio, iaHabilitada })}
        />
      }
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* "Cerrar caso al enviar" — acción sobre el caso. En móvil va en
              su propia fila; en desktop a la izquierda del grupo de botones.
              Como «Enviar ahora» también pasa por la cola (un minuto para
              deshacer), el caso se cierra cuando el correo SALE, no al tocar. */}
          <Toggle
            checked={cerrarCasoAlEnviar}
            onChange={setCerrarCasoAlEnviar}
            label="Cerrar caso al enviar"
            hint="Se cierra cuando el correo sale de verdad (en uno o dos minutos, o a la hora que lo programes)."
            className="sm:max-w-xs"
          />

          {cerrarCasoAlEnviar && (
            <div className="rounded-md border border-ink-700 p-2">
              <SolucionPicker imeis={caso.imeis} value={solucion} onChange={setSolucion} />
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-ghost flex-1 sm:flex-none">
                Cancelar
              </button>
              <button
                onClick={() => setProgramarOpen(true)}
                disabled={submitting || !preview || destinatariosTo.length === 0 || !solucionCompleta}
                className="btn-ghost disabled:opacity-50 flex-1 sm:flex-none"
              >
                Programar envío
              </button>
            </div>
            <button
              onClick={() => enviar()}
              disabled={submitting || !preview || destinatariosTo.length === 0 || !solucionCompleta}
              className="btn-primary disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? "Enviando..." : "Enviar ahora"}
            </button>
          </div>
        </div>
      }
    >
      <div
        className="relative"
        onDragOver={onModalDragOver}
        onDragLeave={onModalDragLeave}
        onDrop={onModalDrop}
      >
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-lg border-2 border-dashed border-emprenet-400 bg-emprenet-500/10 backdrop-blur-sm">
            <div className="text-center px-6">
              <div className="text-3xl mb-2">📥</div>
              <div className="text-lg font-semibold text-emprenet-200">Suelta para adjuntar</div>
              <div className="text-xs text-zinc-200 mt-1">
                Imágenes: te pregunto si van dentro del mensaje o como archivo adjunto. Archivos de más de 10 MB: se envían como enlace seguro de descarga.
              </div>
            </div>
          </div>
        )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Columna izquierda: parametros */}
        <div className="space-y-3">
          {/* PARA — chips removibles + selector (ambos modos) */}
          <ChipsField
            label="Para"
            destinatarios={destinatariosTo}
            onRemove={removerDestTo}
            emptyHint={
              destinatario === "cliente"
                ? "Sin contacto principal — agrega a quién enviar (ej. el asesor comercial)."
                : caso.marca
                  ? `Sin destinatarios. Si la marca "${caso.marca}" tiene proveedor con contactos en /configuracion, cargan automático.`
                  : "Sin destinatarios. Asigna marca al caso o agrega contactos manual."
            }
            selectorOpen={agregarOpenTo}
            onSelectorOpenChange={setAgregarOpenTo}
            contactos={contactosUnificados}
            onPick={pickContactoTo}
            agregarEmail={agregarEmailTo}
            agregarNombre={agregarNombreTo}
            setAgregarEmail={setAgregarEmailTo}
            setAgregarNombre={setAgregarNombreTo}
            onAgregarLibre={agregarLibreTo}
          />

          {/* Saludar a: modo cliente con varios destinatarios, elige a quién
              saluda el correo (el saludo jala ese nombre). */}
          {destinatario === "cliente" && destinatariosTo.length >= 2 && (
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="whitespace-nowrap">Saludar a:</span>
              <select
                className="input text-xs py-1"
                value={saludoEmail || destinatariosTo[0]?.email || ""}
                onChange={(e) => setSaludoEmail(e.target.value)}
              >
                {destinatariosTo.map((d) => (
                  <option key={d.email} value={d.email}>
                    {d.nombre}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Sugerencia asesor comercial al Para — un clic, solo al cliente */}
          {destinatario === "cliente" &&
            preview?.asesor_comercial_sugerido &&
            !destinatariosTo.some(
              (d) =>
                d.email.toLowerCase() ===
                preview.asesor_comercial_sugerido!.email.toLowerCase(),
            ) && (
            <button
              type="button"
              onClick={() => {
                const sug = preview.asesor_comercial_sugerido!;
                setDestinatariosTo((prev) =>
                  prev.some(
                    (d) => d.email.toLowerCase() === sug.email.toLowerCase(),
                  )
                    ? prev
                    : [...prev, { nombre: sug.nombre, email: sug.email }],
                );
              }}
              className="w-full sm:w-auto text-left text-xs px-3 py-1.5 rounded-md border border-dashed border-teal-600 text-teal-400 hover:bg-teal-900/30 transition-colors break-words"
            >
              + Agregar al asesor comercial ({etiquetaAsesor(preview.asesor_comercial_sugerido)})
            </button>
          )}

          {/* Sugerencia asesor comercial como CC — un clic, solo al cliente */}
          {destinatario === "cliente" &&
            preview?.asesor_comercial_sugerido &&
            !destinatariosCc.some(
              (d) =>
                d.email.toLowerCase() ===
                preview.asesor_comercial_sugerido!.email.toLowerCase(),
            ) && (
            <button
              type="button"
              onClick={() => {
                const sug = preview.asesor_comercial_sugerido!;
                setDestinatariosCc((prev) =>
                  prev.some(
                    (d) => d.email.toLowerCase() === sug.email.toLowerCase(),
                  )
                    ? prev
                    : [...prev, { nombre: sug.nombre, email: sug.email }],
                );
              }}
              className="w-full sm:w-auto text-left text-xs px-3 py-1.5 rounded-md border border-dashed border-teal-600 text-teal-400 hover:bg-teal-900/30 transition-colors break-words"
            >
              + Copiar al asesor comercial ({etiquetaAsesor(preview.asesor_comercial_sugerido)})
            </button>
          )}

          {/* CC — chips removibles + selector (ambos modos) */}
          <ChipsField
            label="CC (opcional)"
            destinatarios={destinatariosCc}
            onRemove={removerDestCc}
            emptyHint={
              destinatario === "cliente"
                ? "Sin copias. Si el caso tiene contactos de Seguimiento con email, se cargarán automáticamente. También puedes agregar otros manualmente."
                : "Sin copias. Click + Agregar contacto si quieres incluir a alguien en CC."
            }
            selectorOpen={agregarOpenCc}
            onSelectorOpenChange={setAgregarOpenCc}
            contactos={contactosUnificados}
            onPick={pickContactoCc}
            agregarEmail={agregarEmailCc}
            agregarNombre={agregarNombreCc}
            setAgregarEmail={setAgregarEmailCc}
            setAgregarNombre={setAgregarNombreCc}
            onAgregarLibre={agregarLibreCc}
          />

          {destinatario === "cliente" && (
            <>
{!esReenvio && (
                <>
              <SeccionPlegable
                titulo="Opciones del correo"
                recordarComo="correo-opciones"
                resumen={
                  `${plantilla === "completa" ? "Actualización del caso" : "Seguimiento simple"}` +
                  ` · para ${audiencia === "interno" ? "alguien de Emprenet" : "el cliente"}` +
                  (esCierre ? " · es cierre" : "")
                }
              >
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="text-zinc-400">Tipo de correo:</span>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={plantilla === "completa"}
                    onChange={() => setPlantilla("completa")}
                  />
                  Actualización del caso
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={plantilla === "seguimiento"}
                    onChange={() => setPlantilla("seguimiento")}
                  />
                  Seguimiento simple
                </label>
              </div>

              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="text-zinc-400">¿Para quién es?</span>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={audiencia === "cliente"}
                    onChange={() => setAudiencia("cliente")}
                  />
                  El cliente
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={audiencia === "interno"}
                    onChange={() => setAudiencia("interno")}
                  />
                  Alguien de Emprenet
                </label>
                <span className="text-[11px] text-zinc-500">
                  {audiencia === "interno"
                    ? "habla del equipo en tercera persona (no es suyo)"
                    : "le habla de «tu equipo»"}
                </span>
              </div>

              <Toggle
                checked={esCierre}
                onChange={setEsCierre}
                label="Es cierre del caso (cambia el título y la frase del correo)"
                className="text-sm"
              />
              </SeccionPlegable>
                </>
              )}

              {/* Bitacora interna del caso (read-only) — para tener
                  contexto a la mano mientras se escribe el mensaje. */}
              <BitacoraReferencia
                casoId={caso.id}
                casoUpdatedAt={caso.updated_at}
              />

              <SeccionPlegable
                titulo="Redactar con IA"
                recordarComo="correo-ia-cliente"
                resumen="genera un borrador a partir de la bitácora"
              >
                <GenerarComentarioIA
                  casoId={caso.id}
                  destinatario="cliente"
                  audiencia={audiencia}
                  guiaHtml={comentario}
                  onGenerado={setComentario}
                />
              </SeccionPlegable>

              {/* Historial de borradores formales (reusables) */}
              {historial.length > 0 && (
                <div className="rounded border border-ink-700 bg-ink-950/50 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setHistorialOpen((v) => !v)}
                    className="w-full flex items-center justify-between p-2 hover:bg-ink-800 text-left"
                  >
                    <span className="text-xs text-zinc-400">
                      {historialOpen ? "▼" : "▶"} Borradores guardados ({historial.length})
                    </span>
                    <span className="text-[10px] text-zinc-600">
                      Click "Reusar" para cargar al editor
                    </span>
                  </button>
                  {historialOpen && (
                    <ul className="divide-y divide-ink-700 max-h-40 overflow-y-auto">
                      {historial.map((c) => (
                        <li key={c.id} className="p-2 flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-zinc-500 mb-1">
                              {c.autor_nombre || "—"} ·{" "}
                              {new Date(c.ts).toLocaleString()}
                            </p>
                            <p className="text-xs text-zinc-300 line-clamp-2">
                              {c.texto.slice(0, 200)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => reusarBorrador(c)}
                            className="btn-ghost text-xs text-accent-400 flex-shrink-0"
                          >
                            Reusar
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div>
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between flex-wrap mb-1">
                  <Label>{esReenvio ? "Nota (opcional) antes del reenvío" : "Mensaje al cliente"}</Label>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-zinc-500 min-w-0">
                    {savingDraft && (
                      <span className="text-emerald-400/80">Auto-guardando...</span>
                    )}
                    <span>Se ve igual que en el correo · arrastra o pega imágenes (máx 1 MB)</span>
                  </div>
                </div>
                <RichTextEditor
                  ref={editorClienteRef}
                  disableImageDrop
                  value={comentario}
                  onChange={(html) => setComentario(html)}
                  placeholder={esReenvio ? "Nota opcional. Aparece arriba del mensaje reenviado." : "Escribe el mensaje al cliente. Aparece dentro del correo con el diseño de la empresa."}
                  minHeight={320}
                  onImageRequested={() => {
                    setImagenTarget("cliente");
                    setImagenModalOpen(true);
                  }}
                />
                {!esReenvio && !comentario.trim() && (
                  <p className="text-xs text-amber-400 mt-1">
                    Sin mensaje al cliente, el correo solo lleva el resumen del caso.
                  </p>
                )}
              </div>
            </>
          )}

          {destinatario === "jimi" && (
            <>
              <div>
                <Label>Saludo (nombre del contacto)</Label>
                <input
                  type="text"
                  className="input mt-1"
                  value={contactoProveedor}
                  onChange={(e) => setContactoProveedor(e.target.value)}
                  placeholder="Nombre del contacto principal (opcional)"
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  Abre el correo: «Hola [saludo],». Se llena solo con el primer contacto; vacío
                  queda «Hola,» sin nombre.
                </p>
              </div>

              <div className="border-t border-ink-700 pt-2 space-y-2">
                <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                  Contexto del ticket en el correo
                </span>
                <Toggle
                  checked={incluirSolicitud}
                  onChange={(v) => {
                    setIncluirSolicitud(v);
                    try {
                      localStorage.setItem(INCLUIR_SOLICITUD_KEY, v ? "1" : "0");
                    } catch {
                      /* localStorage bloqueado: no es crítico */
                    }
                  }}
                  label="Incluir el reporte del cliente (Solicitud)"
                  hint="La descripción del problema que trae el caso. Apágalo para que el proveedor se enfoque en lo que tú le pides. Se recuerda en este navegador."
                />
                <Toggle
                  checked={incluirGenerales}
                  onChange={(v) => {
                    setIncluirGenerales(v);
                    try {
                      localStorage.setItem(INCLUIR_GENERALES_KEY, v ? "1" : "0");
                    } catch {
                      /* localStorage bloqueado: no es crítico */
                    }
                  }}
                  label="Incluir saludo y No. de ticket"
                  hint="La frase de apertura (cliente, modelo e IMEIs) y nuestro No. de ticket (TE). El «Hola …» va siempre. Al proveedor casi nunca le sirve: maneja sus propios folios."
                />
              </div>

              {/* Bitacora interna del caso (read-only) — contexto a mano */}
              <BitacoraReferencia
                casoId={caso.id}
                casoUpdatedAt={caso.updated_at}
              />

              <GenerarComentarioIA
                casoId={caso.id}
                destinatario="jimi"
                guiaHtml={comentarioProveedor}
                onGenerado={setComentarioProveedor}
              />

              <div>
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between flex-wrap mb-1">
                  <Label>Mensaje adicional al proveedor (opcional)</Label>
                  <span className="text-[10px] uppercase tracking-wide text-zinc-500 min-w-0">
                    Se ve igual que en el correo · arrastra o pega imágenes (máx 1 MB)
                  </span>
                </div>
                <RichTextEditor
                  ref={editorProveedorRef}
                  disableImageDrop
                  value={comentarioProveedor}
                  onChange={(html) => setComentarioProveedor(html)}
                  placeholder="Texto extra al proveedor (ej. 'ya hicimos pruebas con firmware nuevo y no resuelve', 'cliente urge respuesta'...). Aparece entre la descripción del problema y el cierre."
                  minHeight={240}
                  onImageRequested={() => {
                    setImagenTarget("proveedor");
                    setImagenModalOpen(true);
                  }}
                />
              </div>
            </>
          )}

          <SeccionPlegable
            titulo="Paquete ZIP a incluir"
            abiertoInicial={false}
            recordarComo="correo-paquete"
            resumen={
              paqueteId === ""
                ? "sin paquete"
                : paquetes.find((p) => p.id === paqueteId)?.nombre_zip || "1 paquete"
            }
          >
            <select
              className="input mt-1 text-sm"
              value={paqueteId}
              onChange={(e) =>
                setPaqueteId(e.target.value ? parseInt(e.target.value) : "")
              }
            >
              <option value="">— sin paquete —</option>
              {paquetes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre_zip} ({fmtBytes(p.size_bytes)})
                </option>
              ))}
            </select>
            {paqueteId !== "" && (
              <p className="text-xs text-zinc-500 mt-1">
                Se incluirá enlace y contraseña en el correo.
              </p>
            )}
          </SeccionPlegable>

          <SeccionPlegable
            titulo="Archivos adjuntos"
            abiertoInicial={false}
            recordarComo="correo-adjuntos"
            resumen={
              adjuntos.length === 0
                ? "sin adjuntos"
                : `${adjuntos.length} archivo${adjuntos.length === 1 ? "" : "s"}`
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {adjuntos.length > 0 && (
              <ul className="mt-1 space-y-1">
                {adjuntos.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 rounded border border-ink-700 bg-ink-800 px-2 py-1.5 text-xs"
                  >
                    <span className="text-base">📎</span>
                    <span className="min-w-0 flex-1 break-all text-zinc-100">{a.nombre}</span>
                    <span className="text-zinc-400 shrink-0">{fmtMB(a.bytes)} MB</span>
                    <button
                      type="button"
                      onClick={() => void removerAdjunto(a.id)}
                      className="text-zinc-500 hover:text-red-400 transition"
                      aria-label={`Quitar ${a.nombre}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={subiendoAdjuntos}
              className="mt-1 w-full rounded border-2 border-dashed border-ink-700 px-3 py-2.5 text-xs text-zinc-400 hover:border-emprenet-500 hover:text-zinc-200 disabled:opacity-50 transition"
            >
              {subiendoAdjuntos
                ? "Subiendo..."
                : "+ Adjuntar archivo (o arrastra al modal)"}
            </button>
            <button
              type="button"
              onClick={() => setSelEvidenciasOpen(true)}
              className="mt-1 w-full rounded border border-ink-700 px-3 py-2 text-xs text-zinc-300 hover:border-emprenet-500 hover:text-zinc-100 transition"
            >
              🖼️ Desde evidencias del caso
            </button>
            <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-[10px] text-zinc-500">
              <span>
                Límite de adjuntos: {fmtMB(MAX_ADJUNTO_BYTES)} MB por archivo,{" "}
                {fmtMB(MAX_TOTAL_ADJUNTOS_BYTES)} MB en total. Si pesa más, se envía como enlace seguro de descarga.
              </span>
              {adjuntos.length > 0 && (
                <span
                  className={
                    totalAdjuntosBytes > MAX_TOTAL_ADJUNTOS_BYTES * 0.8
                      ? "text-amber-400 font-medium ml-2 shrink-0"
                      : "text-zinc-400 ml-2 shrink-0"
                  }
                >
                  {fmtMB(totalAdjuntosBytes)} / {fmtMB(MAX_TOTAL_ADJUNTOS_BYTES)} MB
                </span>
              )}
            </div>
          </SeccionPlegable>

          <SeccionPlegable
            titulo="Asunto personalizado"
            abiertoInicial={false}
            recordarComo="correo-asunto"
            resumen={asuntoOverride.trim() ? asuntoOverride : "el automático"}
          >
            <input
              type="text"
              className="input mt-1 text-sm"
              value={asuntoOverride}
              onChange={(e) => {
                asuntoEditadoRef.current = true;
                setAsuntoOverride(e.target.value);
              }}
              placeholder={preview?.asunto || ""}
            />
          </SeccionPlegable>
        </div>

        {/* Columna derecha: preview */}
        <div className="flex flex-col space-y-2 min-h-[260px] md:min-h-[450px]">
          <div className="flex items-center justify-between">
            <Label>Vista previa</Label>
            <div className="flex items-center gap-2">
              {previewLoading && (
                <span className="text-xs text-zinc-500">Generando vista previa...</span>
              )}
              <button
                type="button"
                onClick={() => {
                  setPreviewLight((v) => {
                    const nv = !v;
                    try {
                      localStorage.setItem(PREVIEW_LIGHT_KEY, nv ? "1" : "0");
                    } catch {
                      /* ignore */
                    }
                    return nv;
                  });
                }}
                className="btn-ghost text-xs"
              >
                Fondo: {previewLight ? "Claro" : "Oscuro"}
              </button>
            </div>
          </div>
          <div className="rounded border border-ink-700 bg-ink-800 p-2 text-xs text-zinc-400">
            <div>
              <strong>Asunto:</strong> {asuntoOverride || preview?.asunto || "—"}
            </div>
            <div className="truncate">
              <strong>Para:</strong>{" "}
              {toOverride || preview?.destinatario_emails.join(", ") || "—"}
            </div>
            {cc && (
              <div className="truncate">
                <strong>CC:</strong> {cc}
              </div>
            )}
          </div>
          <div
            className={`email-preview rounded p-2 overflow-auto text-xs flex-1 min-h-[400px] ${
              previewLight
                ? "bg-white text-zinc-800"
                : "bg-ink-800 text-zinc-100 border border-ink-700"
            }`}
            dangerouslySetInnerHTML={{ __html: preview?.html || "" }}
          />
        </div>
      </div>
      </div>

      <InsertarImagenModal
        open={imagenModalOpen}
        onClose={() => setImagenModalOpen(false)}
        casoId={caso.id}
        onInsertar={(r) => {
          const targetRef =
            imagenTarget === "cliente" ? editorClienteRef : editorProveedorRef;
          if (r.tipo === "inline") {
            targetRef.current?.insertImage(r.dataUrl, r.alt);
          } else {
            targetRef.current?.insertHtml(r.bloqueHtml);
          }
        }}
      />

      {/* Modal anidado: programar el envio (atajos tipo Gmail) */}
      <ProgramarEnvioModal
        open={programarOpen}
        onOpenChange={setProgramarOpen}
        onProgramar={(iso) => void enviar(iso)}
      />

      {/* Preguntas al soltar archivos: imagen inline-vs-adjunto y
          archivo grande -> evidencia + ZIP + link (sin salir del modal). */}
      {askImagen && (
        <PreguntaImagenModal
          nombre={askImagen.name}
          sizeBytes={askImagen.size}
          onClose={() => setAskImagen(null)}
          onInline={() => insertarImagenInline(askImagen)}
          onAdjuntar={() => {
            setAskImagen(null);
            void subirAdjuntoNormal(askImagen);
          }}
        />
      )}
      {askOversize && (
        <PreguntaOversizeModal
          file={askOversize}
          subiendo={subiendoAdjuntos}
          onClose={() => setAskOversize(null)}
          onConfirmar={(f) => void subirOversize(f)}
        />
      )}
      {selEvidenciasOpen && (
        <SelectorEvidenciasModal
          casoId={caso.id}
          onClose={() => setSelEvidenciasOpen(false)}
          onElegirImagen={(ev) => {
            setSelEvidenciasOpen(false);
            setDecidirEvidencia(ev);
          }}
          onAdjuntar={(ev) => {
            setSelEvidenciasOpen(false);
            void adjuntarEvidencia(ev);
          }}
        />
      )}
      {decidirEvidencia && (
        <PreguntaVerInsertarModal
          nombre={decidirEvidencia.nombre_original}
          onClose={() => setDecidirEvidencia(null)}
          onVisualizar={() => {
            setVerEvidencia(decidirEvidencia);
            setDecidirEvidencia(null);
          }}
          onInsertar={() => {
            setAskImagenEvidencia(decidirEvidencia);
            setDecidirEvidencia(null);
          }}
        />
      )}
      {verEvidencia && (
        <VisorArchivo
          targets={[
            {
              nombre: verEvidencia.nombre_original,
              mime: verEvidencia.mime,
              url: `/api/evidencias/${verEvidencia.id}/file`,
            },
          ]}
          onClose={() => setVerEvidencia(null)}
        />
      )}
      {askImagenEvidencia && (
        <PreguntaImagenModal
          nombre={askImagenEvidencia.nombre_original}
          sizeBytes={askImagenEvidencia.size_bytes}
          onClose={() => setAskImagenEvidencia(null)}
          onInline={() => void insertarEvidenciaInline(askImagenEvidencia)}
          onAdjuntar={() => void adjuntarEvidencia(askImagenEvidencia)}
        />
      )}
    </Modal>
  );
}
